import {
  getV1CompaniesCompanyIdInvoicesInvoiceIdAddOnCharges,
  getV1CompaniesCompanyIdInvoicesInvoiceIdInvoiceLineItems,
  getV1CompaniesCompanyIdSettlementsSettlementIdAddOnCharges,
  getV1CompaniesCompanyIdSettlementsSettlementIdInvoices,
  patchV1CompaniesCompanyIdInvoicesInvoiceIdAddOnCharges,
  patchV1CompaniesCompanyIdInvoicesInvoiceIdInvoiceLineItems,
  patchV1CompaniesCompanyIdSettlementsSettlementIdAddOnCharges,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { AddOnCharge } from '~hooks/useAddOns/models';
import { InvoiceLineItem } from '~hooks/useInvoiceLineItems/models';
import { Invoice } from '~hooks/useInvoices/models';
import { AddOnChargeDTO } from '~pages/Approvals/DriverPay/helpers';
import connection from '~services/connectionModule';
import { fetchAllApiPages } from '~utils/fetchAllPages';

import { SettlementInvoiceLines } from './models';

const deparseAddOnCharges = (addOnCharges: AddOnChargeDTO[]) =>
  addOnCharges
    .map((addOnCharge) => AddOnCharge.deparse(addOnCharge))
    .filter((addOnCharge) => addOnCharge !== null);

export interface SettlementLinesUpdate {
  addOnChargesByInvoiceId: Map<string, AddOnChargeDTO[]>;
  lineItemsByInvoiceId: Map<string, Array<Partial<InvoiceLineItem>>>;
  settlementAddOnCharges: AddOnChargeDTO[];
}

export default function useSettlementLines() {
  // The API has no read of a settlement's member lines, so they are found by reading the
  // settlement's invoices and keeping the lines that point back at the settlement.
  const getSettlementInvoiceLines = useCallback(
    async (companyId: string, settlementId: string, signal?: AbortSignal) => {
      try {
        const invoices = await fetchAllApiPages(
          (pageQuery, pageSignal) =>
            getV1CompaniesCompanyIdSettlementsSettlementIdInvoices({
              path: { 'company-id': companyId, 'settlement-id': settlementId },
              query: pageQuery,
              signal: pageSignal,
            }),
          (invoiceProto) => invoiceProto,
          { signal },
        );

        const invoiceLines = await Promise.all(
          invoices.map(async (invoiceProto) => {
            const [lineItems, addOnCharges] = await Promise.all([
              fetchAllApiPages(
                (pageQuery, pageSignal) =>
                  getV1CompaniesCompanyIdInvoicesInvoiceIdInvoiceLineItems({
                    path: { 'company-id': companyId, 'invoice-id': invoiceProto.id },
                    query: { ...pageQuery, material_rate_itemized: true },
                    signal: pageSignal,
                  }),
                InvoiceLineItem.parse,
                { signal },
              ),
              fetchAllApiPages(
                (pageQuery, pageSignal) =>
                  getV1CompaniesCompanyIdInvoicesInvoiceIdAddOnCharges({
                    path: { 'company-id': companyId, 'invoice-id': invoiceProto.id },
                    query: pageQuery,
                    signal: pageSignal,
                  }),
                AddOnCharge.parse,
                { signal },
              ),
            ]);

            return new SettlementInvoiceLines(
              Invoice.parse(invoiceProto),
              lineItems.filter((line) => line.settlement?.id === settlementId),
              addOnCharges.filter((line) => line.settlement?.id === settlementId),
            );
          }),
        );

        return invoiceLines.filter((lines) => lines.lineCount > 0);
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_fetch_lines'),
        );
        throw error;
      }
    },
    [],
  );

  const getSettlementAddOnCharges = useCallback(
    async (companyId: string, settlementId: string, signal?: AbortSignal) => {
      try {
        return await fetchAllApiPages(
          (query, pageSignal) =>
            getV1CompaniesCompanyIdSettlementsSettlementIdAddOnCharges({
              path: { 'company-id': companyId, 'settlement-id': settlementId },
              query,
              signal: pageSignal,
            }),
          AddOnCharge.parse,
          { signal },
        );
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_fetch_add_ons'),
        );
        throw error;
      }
    },
    [],
  );

  const updateSettlementLines = useCallback(
    async (
      companyId: string,
      settlementId: string,
      {
        addOnChargesByInvoiceId,
        lineItemsByInvoiceId,
        settlementAddOnCharges,
      }: SettlementLinesUpdate,
    ) => {
      try {
        await Promise.all(
          [...lineItemsByInvoiceId].map(([invoiceId, lineItems]) =>
            patchV1CompaniesCompanyIdInvoicesInvoiceIdInvoiceLineItems({
              path: { 'company-id': companyId, 'invoice-id': invoiceId },
              body: { invoice_line_items: lineItems.map(InvoiceLineItem.deparseUpdate) },
            }),
          ),
        );
        await Promise.all(
          [...addOnChargesByInvoiceId].map(([invoiceId, addOnCharges]) =>
            patchV1CompaniesCompanyIdInvoicesInvoiceIdAddOnCharges({
              path: { 'company-id': companyId, 'invoice-id': invoiceId },
              body: { add_on_charges: deparseAddOnCharges(addOnCharges) },
            }),
          ),
        );

        if (settlementAddOnCharges.length > 0) {
          await patchV1CompaniesCompanyIdSettlementsSettlementIdAddOnCharges({
            path: { 'company-id': companyId, 'settlement-id': settlementId },
            body: { add_on_charges: deparseAddOnCharges(settlementAddOnCharges) },
          });
        }
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_update_lines'),
        );
        throw error;
      }
    },
    [],
  );

  const removeSettlementAddOnCharge = useCallback(
    async (companyId: string, settlementId: string, addOnChargeId: string) => {
      try {
        await patchV1CompaniesCompanyIdSettlementsSettlementIdAddOnCharges({
          path: { 'company-id': companyId, 'settlement-id': settlementId },
          body: { add_on_charges: [{ _destroy: 1, id: addOnChargeId }] },
        });
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_remove_lines'),
        );
        throw error;
      }
    },
    [],
  );

  return {
    getSettlementAddOnCharges,
    getSettlementInvoiceLines,
    removeSettlementAddOnCharge,
    updateSettlementLines,
  };
}
